import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import logo from './logo.svg';
import logoMin from './logoMin.svg';
import { useLanguage } from './useLanguage';

const NettbilNO = () => (
  <>
    <h2>Fra magefølelse til maskinlæring</h2>
    <p>Nettbil er en av Norges ledende digitale markedsplasser for bruktbiler. Hvert år går tusenvis av kjøretøy gjennom plattformen via auksjon. For at modellen skal fungere, må selgeren ha realistiske forventninger til prisen allerede før bilen legges ut. Er verdivurderingen for høy, risikerer man at budet på auksjon ikke aksepteres. Er den upresis, skaper det usikkerhet og friksjon for alle parter. Samtidig er verdivurderingen kun selgerens verktøy: kjøperne ser den aldri, og prisen settes fortsatt av markedet gjennom bud på auksjonen. Modellens jobb er å treffe markedet, ikke å styre det.</p>
    <p>En treffsikker verdivurdering tidlig i prosessen fjerner risiko for kunden og øker sjansen for en vellykket transaksjon. Men tradisjonelt har dette vært et håndverk bygget på erfaring, markedskunnskap og raske sammenligninger. Flow Lab foreslo en løsning: å bygge et system som kan vurdere biler raskere, mer presist og mer konsistent enn det som er mulig manuelt.</p>

    <h2>Fra første versjon til produksjonsplattform</h2>
    <p>Det første vi leverte var en enklere prisingsmodell. Én enkelt algoritme trent på historiske salgsdata. Den fungerte, og den viste at maskinlæring kunne slå manuell prising på konsistens og hastighet. Men den hadde begrensninger: usikkerheten ble estimert med grove marginer, og modellen ble ikke bedre med mindre noen manuelt trente den på nytt.</p>
    <p>Denne fasen handlet om mer enn selve modellen. Hele teamet var involvert, og vi eksperimenterte mye med hvordan og hvor i salgsprosessen verdivurderingen skulle vises til kunden. Målet var hele tiden det samme: å fjerne usikkerhet, gjøre prosessen smidigere og raskere gi selgeren et realistisk bilde av hva som kunne forventes på auksjonen.</p>
    <p>Det ga oss et viktig utgangspunkt og bekreftet at tilnærmingen hadde verdi. Derfra bygde vi videre til det som er i produksjon i dag: en ensemble-plattform som kombinerer flere modelltilnærminger og velger den beste kombinasjonen automatisk. Modellen gir ikke lenger bare ett tall, men et prisintervall basert på kvantilregresjon, en statistisk metode som lar systemet uttrykke hvor sikkert det er på svaret.</p>

    <h2>Et system som holder seg oppdatert</h2>
    <p>Dagens plattform er ikke en statisk modell som ble trent én gang og satt i produksjon. Det er en levende plattform som retrenes med korte mellomrom, basert på ferske salgsdata fra markedet.</p>
    <p>Sesongvariasjoner, markedstrender og endringer i etterspørsel fanges opp automatisk, uten at noen trenger å gjøre manuelle justeringer.</p>
    <p>Hver prediksjon leveres med en konfidensscore, slik at beslutningstakerne vet hvor sikker modellen er på sin vurdering. Er det en vanlig bil med mange sammenlignbare salg? Høy konfidens. Er det en uvanlig konfigurasjon med lite data? Det reflekteres tydelig.</p>

    <h2>Ikke bare hva, men hvorfor</h2>
    <p>Et tilbakevendende spørsmål fra salgsrådgiveren var «hvorfor er prisen slik den er?». Selgere og rådgivere må kunne forklare prisen til kunder, og da trenger de mer enn bare et tall.</p>
    <p>Vi bygde et eget forklarbarhetslag som bryter ned hver prediksjon i forståelige komponenter: Hva betyr kilometerstand for akkurat denne bilen? Hvordan påvirker utstyret prisen? Hva sier markedet om denne modellen akkurat nå?</p>
    <p>Denne transparensen har vist seg å være avgjørende, ikke bare for intern beslutningskvalitet, men for tilliten i hele verdikjeden.</p>

    <h2>Stabil infrastruktur i skyen</h2>
    <p>Hele plattformen kjører på AWS. SageMaker orkestrerer trenings- og inferenspipelines med autoskalering, slik at systemet håndterer både rolige perioder og trafikktopper uten manuell inngripen. Data flyter fra transaksjonsdatabasen til en datalake via Zero-ETL, som forenkler dataintegrasjonen og fjerner behovet for egne ETL-jobber.</p>
    <p>Resultatet er en infrastruktur som er enkel å drifte, skalerer etter behov og gir et solid fundament for ML-pipelines i produksjon.</p>

    <h2>Bygget for virkeligheten</h2>
    <p>Å bygge en god modell er én ting. Å drifte den i produksjon, hver dag, med ekte data og ekte krav, det er noe helt annet.</p>
    <p>Plattformen vi leverte til Nettbil inkluderer:</p>
    <ul>
      <li><strong>Automatisk retrening med korte mellomrom</strong> slik at modellen holder seg oppdatert uten manuell innsats</li>
      <li><strong>Sanntidsprediksjoner</strong> der priser leveres i det øyeblikket de trengs</li>
      <li><strong>Skyggetesting</strong> der nye modeller kjører parallelt og evalueres trygt mot den eksisterende modellen før utrulling</li>
      <li><strong>Personvernhåndtering</strong> der all databehandling følger GDPR med automatisk livssyklusstyring</li>
      <li><strong>Overvåking og rapportering</strong> med dashbord som gir full innsikt i modellens ytelse over tid</li>
    </ul>
    <p>Det er denne helheten som skiller et ML-prosjekt fra et ML-produkt.</p>

    <h2>Resultater som teller</h2>
    <p>Systemet er i full produksjon. Modellen forklarer rundt 94 % av prisvariansen i markedet, og prisintervallene er godt kalibrerte: åtte av ti solgte biler ender innenfor intervallet modellen anga.</p>
    <p>Sammenligningen med første generasjon av modellen viser fremgangen tydelig: gjennomsnittlig avvik er kuttet med en tredjedel, systematisk overprediksjon er mer enn halvert, median-prediksjonen ligger nå midt i markedet, og andelen salg som ender innenfor det predikerte prisintervallet har økt med rundt 35 prosentpoeng.</p>
    <p>Men de viktigste resultatene måles ikke bare i tall. De måles i raskere beslutninger, mer konsistent prising og økt tillit hos både selgere og rådgivere.</p>
    <blockquote>
      <h3>Fremragende bilvurdering på Nettbil</h3>
      <p>«Effektiv tilpasning av selgers forventninger, noe som resulterer i flere vellykkede transaksjoner»</p>
      <a href="https://vend.com/news/interim-report-q2-2025" target="_blank" rel="noopener noreferrer">Se Vend Q2 2025-rapport →</a>
    </blockquote>

    <h2>Et partnerskap, ikke et prosjekt</h2>
    <p>Samarbeidet mellom Flow Lab og Nettbil er ikke et typisk konsulentoppdrag med en definert start og slutt. Det er et løpende partnerskap der vi siden januar 2023 kontinuerlig har forbedret og utvidet plattformen.</p>
    <p>Det som startet som en prisingsmodell har vokst til en fullverdig ML-plattform som dekker prediksjon, forklaring og analyse. Og vi er langt fra ferdige.</p>
    <p>For Flow Lab er dette et eksempel på hva vi mener med «bygget for å levere, ikke rådgi». Vi tror på at den beste måten å vise verdien av AI er å sette det i produksjon og la resultatene snakke for seg.</p>
    <p><em>Skrevet av mennesker, polert med Claude.</em></p>
  </>
);

const NettbilEN = () => (
  <>
    <h2>From Gut Feeling to Machine Learning</h2>
    <p>Nettbil is one of Norway's leading digital marketplaces for used cars. Every year, thousands of vehicles pass through the platform via auction. For the model to work, the seller must have realistic expectations about the price before the car is listed. If the valuation is too high, there is a risk that the auction bid will not be accepted. If it is imprecise, it creates uncertainty and friction for all parties. At the same time, the valuation is the seller's tool only: buyers never see it, and the price is still set by the market through bids at auction. The model's job is to predict the market, not to steer it.</p>
    <p>An accurate valuation early in the process removes risk for the customer and increases the chance of a successful transaction. But traditionally, this has been a craft built on experience, market knowledge, and quick comparisons. Flow Lab proposed a solution: to build a system that can evaluate cars faster, more precisely, and more consistently than what is possible manually.</p>

    <h2>From First Version to Production Platform</h2>
    <p>The first thing we delivered was a simpler pricing model. A single algorithm trained on historical sales data. It worked, and it showed that machine learning could beat manual pricing on consistency and speed. But it had limitations: uncertainty was estimated with rough margins, and the model did not improve unless someone manually retrained it.</p>
    <p>This phase was about more than the model itself. The whole team was involved, and we experimented extensively with how and where in the selling process the valuation should be shown to the customer. The goal was always the same: to remove uncertainty, make the process smoother, and more quickly give the seller a realistic picture of what to expect at auction.</p>
    <p>This gave us an important starting point and confirmed that the approach had value. From there, we built on to what is in production today: an ensemble platform that combines multiple model approaches and automatically selects the best combination. The model no longer just gives a single number, but a price range based on quantile regression, a statistical method that lets the system express how confident it is in its answer.</p>

    <h2>A System That Stays Up to Date</h2>
    <p>Today's platform is not a static model that was trained once and put into production. It is a living platform that is retrained at short intervals, based on fresh sales data from the market.</p>
    <p>Seasonal variations, market trends, and changes in demand are captured automatically, without anyone needing to make manual adjustments.</p>
    <p>Every prediction comes with a confidence score, so that decision-makers know how confident the model is in its assessment. Is it a common car with many comparable sales? High confidence. Is it an unusual configuration with little data? That is clearly reflected.</p>

    <h2>Not Just What, but Why</h2>
    <p>A recurring question from the sales advisor was "why is the price the way it is?" Sellers and advisors need to be able to explain the price to customers, and they need more than just a number.</p>
    <p>We built a dedicated explainability layer that breaks down each prediction into understandable components: What does mileage mean for this particular car? How does the equipment affect the price? What does the market say about this model right now?</p>
    <p>This transparency has proven to be crucial, not only for internal decision quality, but for trust throughout the entire value chain.</p>

    <h2>Stable Cloud Infrastructure</h2>
    <p>The entire platform runs on AWS. SageMaker orchestrates training and inference pipelines with autoscaling, so the system handles both quiet periods and traffic peaks without manual intervention. Data flows from the transaction database to a data lake via Zero-ETL, which simplifies data integration and eliminates the need for dedicated ETL jobs.</p>
    <p>The result is infrastructure that is easy to operate, scales as needed, and provides a solid foundation for ML pipelines in production.</p>

    <h2>Built for Reality</h2>
    <p>Building a good model is one thing. Operating it in production, every day, with real data and real requirements, that is something entirely different.</p>
    <p>The platform we delivered to Nettbil includes:</p>
    <ul>
      <li><strong>Automatic retraining at short intervals</strong> so the model stays up to date without manual effort</li>
      <li><strong>Real-time predictions</strong> where prices are delivered the moment they are needed</li>
      <li><strong>Shadow testing</strong> where new models run in parallel and are safely evaluated against the existing model before rollout</li>
      <li><strong>Privacy handling</strong> where all data processing follows GDPR with automatic lifecycle management</li>
      <li><strong>Monitoring and reporting</strong> with dashboards that provide full insight into the model's performance over time</li>
    </ul>
    <p>It is this completeness that separates an ML project from an ML product.</p>

    <h2>Results That Matter</h2>
    <p>The system is in full production. The model explains around 94% of the price variance in the market, and the price ranges are well calibrated: eight out of ten cars sold end up within the range the model predicted.</p>
    <p>The comparison with the first generation of the model shows the progress clearly: average deviation has been cut by a third, systematic overprediction has been more than halved, the median prediction now sits right at market level, and the share of sales ending up within the predicted price range has increased by around 35 percentage points.</p>
    <p>But the most important results are not measured only in numbers. They are measured in faster decisions, more consistent pricing, and increased trust among both sellers and advisors.</p>
    <blockquote>
      <h3>Car Valuation Excellence on Nettbil</h3>
      <p>"Efficiently aligning seller expectations, resulting in more successful transactions"</p>
      <a href="https://vend.com/news/interim-report-q2-2025" target="_blank" rel="noopener noreferrer">View Vend Q2 2025 Report →</a>
    </blockquote>

    <h2>A Partnership, Not a Project</h2>
    <p>The collaboration between Flow Lab and Nettbil is not a typical consulting engagement with a defined start and end. It is an ongoing partnership where we have continuously improved and expanded the platform since January 2023.</p>
    <p>What started as a pricing model has grown into a full-fledged ML platform covering prediction, explanation, and analysis. And we are far from finished.</p>
    <p>For Flow Lab, this is an example of what we mean by "built to deliver, not advise." We believe that the best way to show the value of AI is to put it into production and let the results speak for themselves.</p>
    <p><em>Drafted by humans, polished with Claude.</em></p>
  </>
);

const posts = [
  {
    slugs: { no: 'nettbil-bilprising', en: 'nettbil-car-pricing' },
    dateISO: '2026-08-13',
    no: {
      metaTitle: 'Hvordan Flow Lab har hjulpet Nettbil med bilprising | Flow Lab',
      metaDescription: 'Hvordan Flow Lab bygde en AI-drevet prisingsplattform for Nettbil. Jevnlig retrent, forklarbar og i full produksjon.',
      title: 'Hvordan Flow Lab har hjulpet Nettbil med bilprising',
      excerpt: 'Fra magefølelse til maskinlæring: slik bygde vi en prisingsplattform som retrenes jevnlig, forklarer sine beslutninger og kjører i full produksjon.',
      date: '13. august 2026',
      Content: NettbilNO,
    },
    en: {
      metaTitle: 'How Flow Lab Helped Nettbil with Car Pricing | Flow Lab',
      metaDescription: 'How Flow Lab built an AI-powered pricing platform for Nettbil. Regularly retrained, explainable, and in full production.',
      title: 'How Flow Lab Helped Nettbil with Car Pricing',
      excerpt: 'From gut feeling to machine learning: how we built a pricing platform that retrains regularly, explains its decisions, and runs in full production.',
      date: 'August 13, 2026',
      Content: NettbilEN,
    },
  },
];

const postBySlug = {};
posts.forEach((post) => {
  postBySlug[post.slugs.no] = post;
  postBySlug[post.slugs.en] = post;
});

const BlogPost = () => {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const post = postBySlug[slug];
  const c = post ? post[lang] : null;

  useEffect(() => {
    if (!c) return;
    document.title = c.metaTitle;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.content = c.metaDescription;
    window.scrollTo(0, 0);
  }, [c]);

  useEffect(() => {
    if (!c || !post) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: c.title,
      datePublished: post.dateISO,
      author: { '@type': 'Organization', name: 'Flow Lab' },
      publisher: { '@type': 'Organization', name: 'Flow Lab', url: 'https://flowlab.no' },
      description: c.metaDescription,
    });
    document.head.appendChild(script);
    return () => document.head.removeChild(script);
  }, [c, post]);

  if (!post) return null;

  const Content = c.Content;
  const backHref = lang === 'no' ? '/' : '/en/';
  const backText = lang === 'no' ? '← Tilbake til forsiden' : '← Back to homepage';

  return (
    <>
      <header className="blog-header">
        <Link to={backHref} className="logo-link" aria-label="Flow Lab">
          <img src={logo} alt="Flow Lab" className="top-left-logo logo-desktop" width="150" height="45" />
          <img src={logoMin} alt="Flow Lab" className="top-left-logo logo-mobile" width="150" height="45" />
        </Link>
        <nav className="lang-switcher">
          <Link
            to={`/blogg/${post.slugs.no}`}
            className={lang === 'no' ? 'active' : ''}
            onClick={() => localStorage.setItem('lang-preference', 'no')}
          >
            NO
          </Link>
          <span className="lang-divider">|</span>
          <Link
            to={`/en/blog/${post.slugs.en}`}
            className={lang === 'en' ? 'active' : ''}
            onClick={() => localStorage.setItem('lang-preference', 'en')}
          >
            EN
          </Link>
        </nav>
      </header>

      <article className="blog-post">
        <div className="blog-post-meta">
          <Link to={backHref} className="blog-back-link">{backText}</Link>
          <time className="blog-post-date" dateTime={post.dateISO}>{c.date}</time>
        </div>
        <h1>{c.title}</h1>
        <Content />
      </article>
    </>
  );
};

export { posts };
export default BlogPost;
